"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";

/**
 * Recreación fiel de reference/diseno-handoff/.../Acceso.dc.html (spec exacta
 * en docs/fases/00-... del plan de Fase 0). Corrige §8.1 del brief, que
 * describía proporciones/copy distintos al handoff real — ver
 * docs/fases/01-acceso-usuarios.md.
 */
export default function LoginPage() {
  const [modo, setModo] = useState<"equipo" | "cliente">("equipo");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [recordar, setRecordar] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [ayudaPassword, setAyudaPassword] = useState(false);
  const [pidiendoEnlace, setPidiendoEnlace] = useState(false);
  const [avisoRecuperacion, setAvisoRecuperacion] = useState<string | null>(null);

  /**
   * Pide el enlace para poner otra contraseña.
   *
   * La respuesta del servidor es la misma exista o no la cuenta —si dijera
   * "ese correo no está" cualquiera podría averiguar quién trabaja aquí
   * probando direcciones—, así que aquí tampoco se distingue.
   */
  async function pedirEnlace() {
    const correo = email.trim();
    if (!correo) {
      setAvisoRecuperacion("Escribe arriba tu correo y vuelve a pulsar.");
      return;
    }
    setPidiendoEnlace(true);
    const res = await fetch("/api/cuenta/recuperar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: correo }),
    }).catch(() => null);
    setPidiendoEnlace(false);
    const cuerpo = res ? await res.json().catch(() => null) : null;
    setAvisoRecuperacion(
      typeof cuerpo?.mensaje === "string" ? cuerpo.mensaje : "No se ha podido pedir el enlace. Vuelve a intentarlo en un momento."
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    const resultado = await signIn("credentials", {
      email,
      password,
      recordar: String(recordar),
      redirect: false,
    });
    setEnviando(false);
    if (resultado?.error === "BLOQUEADO") {
      setError("Demasiados intentos. Vuelve a probar en 15 minutos.");
      return;
    }
    if (resultado?.error) {
      setError("El correo o la contraseña no son correctos.");
      return;
    }
    window.location.href = "/";
  }

  return (
    <div className="flex min-h-screen" style={{ fontFamily: "var(--font-sans)", color: "#333333" }}>
      <div
        className="flex flex-col justify-between"
        style={{
          flex: "1 1 520px",
          minWidth: 0,
          background: "var(--wash-hero)",
          padding: "48px 56px",
          boxSizing: "border-box",
        }}
      >
        <img src="/logos/caoba-logo.svg" alt="Caoba" style={{ height: 32, display: "block", alignSelf: "flex-start" }} />

        <div style={{ maxWidth: 380, width: "100%", alignSelf: "center" }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "var(--tracking-widest)",
              textTransform: "uppercase",
              color: "var(--warm-300)",
              marginBottom: 12,
            }}
          >
            Acceso
          </div>
          <h1
            style={{
              fontFamily: "var(--font-editorial)",
              fontWeight: 400,
              fontSize: 34,
              lineHeight: 1.18,
              color: "var(--text-strong)",
              margin: "0 0 8px 0",
            }}
          >
            El taller, al día.
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--text-muted)", margin: "0 0 28px 0" }}>
            Presupuestos, encargos y facturas en un solo sitio.
          </p>

          <div className="flex" style={{ border: "1px solid var(--border-hairline)", marginBottom: 24 }}>
            <button
              type="button"
              onClick={() => setModo("equipo")}
              style={{
                flex: 1,
                background: modo === "equipo" ? "#FFFFFF" : "transparent",
                color: modo === "equipo" ? "var(--text-strong)" : "var(--text-muted)",
                border: "none",
                borderBottom: `2px solid ${modo === "equipo" ? "var(--caoba-accent)" : "transparent"}`,
                padding: "11px 0",
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                letterSpacing: "var(--tracking-wide)",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Equipo
            </button>
            <button
              type="button"
              onClick={() => setModo("cliente")}
              style={{
                flex: 1,
                background: modo === "cliente" ? "#FFFFFF" : "transparent",
                color: modo === "cliente" ? "var(--text-strong)" : "var(--text-muted)",
                border: "none",
                borderLeft: "1px solid var(--border-soft)",
                borderBottom: `2px solid ${modo === "cliente" ? "var(--caoba-accent)" : "transparent"}`,
                padding: "11px 0",
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                letterSpacing: "var(--tracking-wide)",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Clientes
            </button>
          </div>

          <form onSubmit={onSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "var(--tracking-widest)",
                  textTransform: "uppercase",
                  color: "var(--warm-300)",
                  marginBottom: 6,
                }}
              >
                Correo
              </label>
              <input
                type="email"
                inputMode="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  height: 44,
                  padding: "0 14px",
                  border: "1px solid var(--border-hairline)",
                  background: "#FFFFFF",
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  color: "var(--text-strong)",
                  borderRadius: 0,
                }}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "var(--tracking-widest)",
                  textTransform: "uppercase",
                  color: "var(--warm-300)",
                  marginBottom: 6,
                }}
              >
                Contraseña
              </label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  height: 44,
                  padding: "0 14px",
                  border: "1px solid var(--border-hairline)",
                  background: "#FFFFFF",
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  color: "var(--text-strong)",
                  borderRadius: 0,
                }}
              />
            </div>

            {error && (
              <div style={{ fontSize: 12.5, color: "var(--state-error)", marginBottom: 12 }}>{error}</div>
            )}

            <div className="flex items-center justify-between" style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12.5,
                  color: "var(--warm-700)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={recordar}
                  onChange={(e) => setRecordar(e.target.checked)}
                  style={{ accentColor: "var(--caoba-accent)", width: 14, height: 14 }}
                />
                Recuérdame
              </label>
              <button
                type="button"
                onClick={() => setAyudaPassword((v) => !v)}
                style={{
                  fontSize: 12.5,
                  color: "var(--text-muted)",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontFamily: "var(--font-sans)",
                }}
              >
                ¿Has olvidado la contraseña?
              </button>
            </div>

            {ayudaPassword && (
              // Antes esto solo decía "pídeselo al propietario", porque no
              // había servidor de correo. Ahora lo hay: se manda un enlace
              // a la dirección de la cuenta.
              <div style={{ fontSize: 12.5, color: "var(--warm-700)", lineHeight: 1.6, marginTop: -14, marginBottom: 24 }}>
                {avisoRecuperacion ?? (
                  <>
                    Te mandamos un enlace a{" "}
                    <strong style={{ fontWeight: 500 }}>{email.trim() || "tu correo"}</strong> para que elijas una nueva.{" "}
                    <button
                      type="button"
                      disabled={pidiendoEnlace}
                      onClick={pedirEnlace}
                      style={{
                        fontSize: 12.5,
                        color: "var(--text-strong)",
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "var(--font-sans)",
                      }}
                    >
                      {pidiendoEnlace ? "Enviando…" : "Mandar el enlace"}
                    </button>
                  </>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={enviando}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                background: "var(--caoba-accent)",
                color: "var(--text-strong)",
                height: 48,
                border: "none",
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: "var(--tracking-wide)",
                textTransform: "uppercase",
                cursor: "pointer",
                borderRadius: 0,
              }}
            >
              Entrar
            </button>
          </form>

          <p style={{ fontSize: 12, color: "var(--warm-300)", marginTop: 16, lineHeight: 1.6 }}>
            {modo === "equipo"
              ? "Sin registro público: las cuentas del equipo las crea el propietario en Ajustes."
              : "Tu acceso llega por correo al aceptar tu primer presupuesto. ¿No lo encuentras? Escríbenos."}
          </p>
        </div>

        <div style={{ fontSize: 11, color: "var(--warm-300)", letterSpacing: "var(--tracking-wide)" }}>
          CAOBA · NIGRÁN, PONTEVEDRA · MADE IN GALICIA
        </div>
      </div>

      <div
        className="relative overflow-hidden hidden md:flex items-end"
        style={{ flex: "1 1 480px", minWidth: 320 }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/img/aparador-ena-lacado.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          aria-hidden
        />
        <div className="absolute inset-0" style={{ background: "var(--scrim-bottom)" }} aria-hidden />
        <div className="relative" style={{ padding: "40px 44px", color: "#FFFFFF" }}>
          <div style={{ fontFamily: "var(--font-editorial)", fontWeight: 400, fontSize: 26, lineHeight: 1.3, maxWidth: 420 }}>
            Ponemos nuestro corazón en cada mueble que creamos.
          </div>
          <div style={{ fontSize: 12, letterSpacing: "var(--tracking-wide)", marginTop: 10, color: "rgba(255,255,255,.72)" }}>
            APARADOR ENA · ROBLE LACADO
          </div>
        </div>
      </div>
    </div>
  );
}
